import type { TFunction } from 'i18next';
import type {
  SystemUsagePeriod,
  SystemUsagePeriodStatsDto,
} from '@/api/ai-system-usage';
import { toCalendarDay } from '@/lib/helpers/date-string';
import {
  monthLabelKey,
  resolveUsagePeriod,
  shiftUsagePeriod,
  toUsagePeriod,
  type UsagePeriod,
} from '@/lib/helpers/usage-period';
import type { AiUsageSearch } from '@/routes/-shared/aiUsageSearchSchema';

/**
 * I periodi della pagina, gli stessi valori di `SystemUsagePeriod` di
 * `@repo/api` (ridichiarati: dalla webapp il package si importa solo come
 * tipo). L'ordine è quello del controllo segmentato.
 */
export const AI_USAGE_PERIODS = [
  'month',
  'last-3-months',
  'year',
] as const satisfies readonly SystemUsagePeriod[];

export const DEFAULT_AI_USAGE_PERIOD: SystemUsagePeriod = 'month';

/**
 * Mesi coperti da «ultimi 3 mesi»: il mese di riferimento e i due prima.
 * Solo il ripiego finché manca la risposta, che porta l'intervallo vero.
 */
const LAST_MONTHS_SPAN = 3;

/** Ciò che la pagina chiede all'API: periodo e mese di riferimento. */
export interface AiUsageSelection extends UsagePeriod {
  period: SystemUsagePeriod;
}

export function isAiUsagePeriod(value: unknown): value is SystemUsagePeriod {
  return (AI_USAGE_PERIODS as readonly unknown[]).includes(value);
}

/**
 * La selezione mostrata: periodo, anno e mese dell'URL se validi, altrimenti
 * il mese corrente (`resolveUsagePeriod`: un mese nel futuro o un anno fuori
 * dal selettore tornano al corrente).
 */
export function resolveAiUsageSelection(
  search: AiUsageSearch,
  current: UsagePeriod
): AiUsageSelection {
  return {
    period: isAiUsagePeriod(search.period)
      ? search.period
      : DEFAULT_AI_USAGE_PERIOD,
    ...resolveUsagePeriod(search, current),
  };
}

const monthYearLabel = (period: UsagePeriod, t: TFunction) =>
  `${t(monthLabelKey(period.month))} ${period.year}`;

/** L'intervallo della risposta e la selezione a cui si riferisce. */
export type AiUsagePeriodRange = Pick<
  SystemUsagePeriodStatsDto,
  'period' | 'year' | 'month' | 'from' | 'to'
>;

interface MonthBounds {
  first: UsagePeriod;
  last: UsagePeriod;
}

const isRangeOfSelection = (
  range: AiUsagePeriodRange,
  selection: AiUsageSelection
) =>
  range.period === selection.period &&
  range.year === selection.year &&
  range.month === selection.month;

/**
 * Primo e ultimo mese degli «ultimi 3 mesi»: dall'intervallo della risposta
 * (lo calcola il backend) quando è quello della selezione; durante il
 * caricamento, o con la risposta della selezione precedente
 * (`keepPreviousData`), dal calcolo locale.
 */
function resolveLastMonthsBounds(
  selection: AiUsageSelection,
  range: AiUsagePeriodRange | null | undefined
): MonthBounds {
  // Il client converte i `YYYY-MM-DD` in `Date`: `toCalendarDay` li riporta
  // al giorno.
  const from =
    range && isRangeOfSelection(range, selection)
      ? toCalendarDay(range.from)
      : null;
  const to = range && from ? toCalendarDay(range.to) : null;
  if (from && to)
    return { first: toUsagePeriod(from), last: toUsagePeriod(to) };
  return {
    first: shiftUsagePeriod(selection, 1 - LAST_MONTHS_SPAN),
    last: selection,
  };
}

/**
 * Il periodo a parole: «Settembre 2026», «Luglio – Settembre 2026»
 * (con l'anno su entrambi i lati se il trimestre lo scavalca), «2026».
 */
export function formatAiUsagePeriodLabel(
  selection: AiUsageSelection,
  t: TFunction,
  range?: AiUsagePeriodRange | null
): string {
  if (selection.period === 'year') return String(selection.year);
  if (selection.period === 'month') return monthYearLabel(selection, t);
  const { first, last } = resolveLastMonthsBounds(selection, range);
  const start =
    first.year === last.year
      ? t(monthLabelKey(first.month))
      : monthYearLabel(first, t);
  return `${start} – ${monthYearLabel(last, t)}`;
}
